import { useUpload } from "@context/UploadContext";
import { audioExtract } from "@utils/video";
import { useRouter } from "next/navigation";
import { ChangeEvent, DragEvent } from "react";
import apiConfig from "@config/api"; // Import the new config file

/**
 * Interface for the parameters required to upload the XLSX file.
 */
interface UploadXlsxParams {
  accountID: string,
  projectID: string,
  practice: string,
  quarter: string,
}

/**
 * Custom hook that provides functionality for file upload and processing.
 *
 * @returns {Object} An object containing the following functions:
 * - `uploadFiles`: Handles the file upload process, including validation, conversion, and API calls.
 * - `removeFile`: Removes a file from the list of files to be uploaded.
 * - `changeFile`: Handles file selection or drag-and-drop events to update the list of files.
 * - `reset`: Resets the upload state, clearing all files and notifications.
 */
const useAPI = () => {
  const { push } = useRouter();
  const {
    files,
    setFiles,
    complete,
    setComplete,
    setUploading,
    setVideoURL,
    setCsvUrls,
    setNotification,
    setReport,
    notification,
  } = useUpload();

  const updateProgress = (progress: number) => {
    if (notification) {
      setNotification({
        ...notification,
        progress,
      });
    }
  };

  const reset = () => {
    setFiles([]);
    setComplete(false);
    setUploading(false);
    setVideoURL(null);
    setCsvUrls([]);
    setNotification(null);
  };

  const uploadFiles = async ({
      accountID,
      projectID,
      practice,
      quarter,
  }: UploadXlsxParams) => {
    if (complete) return;

    try {
      setUploading(true);
      updateProgress(0);

      setNotification({
        status: "info",
        message: "Preparing file for upload...",
        progress: 0,
      });

      const isMp4 = files[0].type === "video/mp4";
      const isCSV = files[0].type === "text/csv";
      const isXLSX =
        files[0].type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      if (isMp4) {
        setNotification({
          status: "info",
          message: "Converting video to audio...",
        });
        const videoUrl = URL.createObjectURL(files[0]);
        setVideoURL(videoUrl);
        const audioFile = await audioExtract(files[0]);
        const formData = new FormData();
        formData.append("files", audioFile as File);
        const uploadResponse = await fetch(apiConfig.baseUrl + apiConfig.endpoints.upload, {
          method: "POST",
          body: formData,
        });
        const { data: uploadData } = await uploadResponse.json();
        if (!uploadData) {
          throw new Error("Failed to upload file.");
        }
        setNotification({
          status: "info",
          message: "Files uploaded successfully. Processing content...",
        });

        const reportResponse = await fetch(apiConfig.baseUrl + apiConfig.endpoints.report, {
          method: "POST",
          body: JSON.stringify({ files: [uploadData.filename] }),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const { data: reportData } = await reportResponse.json();
        if (!reportData) {
          throw new Error("Failed to generate report.");
        }
        console.info("🚀 ~ uploadFiles ~ reportData:", reportData);
        setReport(reportData);

        setNotification({
          status: "success",
          message:
            "Files processing complete. You will be redirected to results shortly.",
        });
      }

      if (isXLSX) {
        // Stage 1: Prepare upload (0-20%)
        console.log("Starting XLSX upload process", {
          filename: files[0].name,
          fileSize: files[0].size,
        });
        updateProgress(20);
        setNotification({
          status: "info",
          message: "Uploading file...",
          progress: 20,
        });

        const formData = new FormData();
        formData.append("file", files[0]);
        formData.append("account_id", accountID)
        formData.append("project_id", projectID)
        formData.append("practice", practice)
        formData.append("quarter", quarter)


        // Stage 2: Upload XLSX (20-60%)
        console.log(
          "Sending XLSX to endpoint:",
          apiConfig.baseUrl + apiConfig.endpoints.uploadXlsx,
        );
        try {
          const uploadResponse = await fetch(
            apiConfig.baseUrl + apiConfig.endpoints.uploadXlsx,
            {
              method: "POST",
              body: formData,
            },
          );

          console.log("XLSX upload response received", {
            status: uploadResponse.status,
            statusText: uploadResponse.statusText,
            headers: Object.fromEntries(uploadResponse.headers.entries()),
          });

          if (!uploadResponse.ok) {
            if (uploadResponse.status === 400) {
              let errorData;
              try {
                errorData = await uploadResponse.json();
              } catch (jsonError) {
                // This catch will only run if .json() fails
                console.error("Could not parse JSON from 400 error response", jsonError);
                throw new Error(`The server returned a validation error, but the specific message could not be read.`);
              }
              // If parsing was successful, throw the detailed error from the backend.
              // This will be caught by the main try...catch block of the uploadFiles function.
              throw new Error(errorData.detail || "An unknown validation error occurred.");
            }
            const errorText = await uploadResponse
              .text()
              .catch(() => "Could not read error response");
            console.error("XLSX upload failed", {
              status: uploadResponse.status,
              error: errorText,
            });
            throw new Error(
              `Failed to upload XLSX file: ${uploadResponse.status} ${uploadResponse.statusText}`,
            );
          }

          // Check if the response is a PDF (already processed file)
          const contentType = uploadResponse.headers.get("content-type");

          if (contentType && contentType.includes("application/pdf")) {
            console.log("Received PDF response directly from upload");
            updateProgress(90);
            setNotification({
              status: "info",
              message: "File already processed, preparing PDF...",
              progress: 90,
            });

            try {
              const pdfBlob = await uploadResponse.blob();
              console.log("PDF blob received", {
                size: pdfBlob.size,
                type: pdfBlob.type,
              });
              const pdfUrl = URL.createObjectURL(pdfBlob);

              setReport({
                title: ["XLSX Analysis Report"],
                transcript: "",
                url: pdfUrl,
                sections: [],
              });

              updateProgress(100);
              setNotification({
                status: "success",
                message: "Report ready. You will be redirected shortly.",
                progress: 100,
              });
            } catch (pdfError) {
              console.error("Error processing PDF blob", pdfError);
              throw pdfError;
            }
          } else {
            console.log("Received JSON response, processing further");
            let responseJson;
            try {
              const responseText = await uploadResponse.text();
              console.log("Response text:", responseText);
              responseJson = JSON.parse(responseText);
            } catch (parseError) {
              console.error("Error parsing JSON response", parseError);
              throw new Error(
                "Failed to parse server response: " +
                  (parseError as Error).message,
              );
            }

            if (!responseJson || responseJson.Status !== "success") {
              throw new Error("Failed to process XLSX file.");
            }

            updateProgress(60);
            setNotification({
              status: "info",
              message: "Processing file...",
              progress: 60,
            });

            // Stage 3: Get Report (60-90%)
            console.log("Requesting PDF report with filename:", files[0].name);
            try {
              const reportResponse = await fetch(
                apiConfig.baseUrl + apiConfig.endpoints.pdfReport,
                {
                  method: "POST",
                  body: JSON.stringify({ filename: files[0].name, quarter, practice, accountID, projectID }),
                  headers: {
                    "Content-Type": "application/json",
                  },
                },
              );

              console.log("PDF report response received", {
                status: reportResponse.status,
                statusText: reportResponse.statusText,
                headers: Object.fromEntries(reportResponse.headers.entries()),
              });

              if (!reportResponse.ok) {
                const errorText = await reportResponse
                  .text()
                  .catch(() => "Could not read error response");
                console.error("PDF report generation failed", {
                  status: reportResponse.status,
                  error: errorText,
                });
                throw new Error(
                  `Failed to generate PDF report: ${reportResponse.status} ${reportResponse.statusText}`,
                );
              }

              updateProgress(90);
              setNotification({
                status: "info",
                message: "Finalizing...",
                progress: 90,
              });
              const responseJson = await reportResponse.json()
              setReport({
                title: ["XLSX Analysis Report"],
                transcript: "",
                url: responseJson.url,
                sections: [],
              });
            } catch (reportError) {
              console.error("Error during PDF report generation", reportError);
              throw reportError;
            }
          }
        } catch (uploadError) {
          console.error("Error during XLSX upload process", uploadError);
          throw uploadError;
        }

        updateProgress(100);
        setNotification({
          status: "success",
          message:
            "XLSX file processed successfully. You will be redirected to results shortly.",
          progress: 100,
        });
      }

      if (isCSV) {
        const csvUrls = files.map((file) => ({
          name: file.name,
          url: URL.createObjectURL(file),
        }));
        setCsvUrls(csvUrls);
      }

      setUploading(false);
      setComplete(true);

      await new Promise((resolve) => setTimeout(resolve, 2000)); // wait to redirect

      push("/report");
    } catch (e) {
      setNotification({
        status: "error",
        message: (e as Error).message,
        progress: 0,
      });
      setUploading(false);
    }
  };

  const changeFile = (
    e: ChangeEvent<HTMLInputElement> | DragEvent<HTMLElement>,
  ) => {
    let newFiles: File[] = [];
    if ("dataTransfer" in e) {
      if (e.dataTransfer) {
        newFiles = Array.from(e.dataTransfer.files);
      }
    } else if (e.target.files?.length) {
      newFiles = Array.from(e.target.files);
    }
    if (newFiles.length) {
      const isValid =
        (newFiles.length === 1 && newFiles[0].type === "video/mp4") ||
        (newFiles.length === 1 &&
          newFiles[0].type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
        newFiles.every((file) => file.type === "text/csv");

      if (!isValid) {
        setNotification({
          status: "error",
          message:
            "You can only upload one video file, one XLSX file, or multiple CSV files.",
        });
        return;
      }
      setFiles(newFiles);
    }
  };

  const removeFile = () => {
    setFiles([]);
  };

  return { uploadFiles, removeFile, changeFile, reset };
};

export default useAPI;
